import type { LucideIcon } from 'lucide-react';
import { ArrowDown, ArrowUp, CircleHelp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { HoverHint } from '@/features/engagement/components/EngagementNotReady';
import type { DashboardKpiIconTone } from '../dashboardKpis';

const ICON_TONE: Record<DashboardKpiIconTone, string> = {
  blue: 'text-sky-400',
  cyan: 'text-sky-400',
  pink: 'text-rose-400',
  purple: 'text-violet-400',
  rose: 'text-red-400',
};

export interface DashboardKpiCardProps {
  label: string;
  value: string;
  /** Period-over-period change in percent. Omitted when the API has no delta. */
  delta?: number | null;
  /** Outline glyph — left of label (Figma). */
  icon?: LucideIcon;
  iconTone?: DashboardKpiIconTone;
  /** Short definition / data-source — shown on hover. */
  hint?: string;
  /** Show Coming soon treatment (Visitors until PostHog). */
  comingSoon?: boolean;
  /** Show Unavailable treatment (PostHog outage / credentials). */
  unavailable?: boolean;
  /**
   * Prior comparison window label, e.g. "May 13 – May 19".
   * Rendered as muted “vs …” after the delta % (Figma footer).
   */
  comparisonLabel?: string;
  /** Unused on Figma KPI cards — kept optional for tests/compat. */
  sparkline?: number[];
  className?: string;
}

/**
 * Figma KPI tile: [icon] label → large value → ↑ % + vs prior range.
 */
export function DashboardKpiCard({
  label,
  value,
  delta = null,
  icon: Icon,
  iconTone = 'blue',
  hint,
  comingSoon = false,
  unavailable = false,
  comparisonLabel,
  className,
}: DashboardKpiCardProps) {
  const shell = comingSoon || unavailable;
  const hasDelta = !shell && delta != null && Number.isFinite(delta);
  const rising = (delta ?? 0) >= 0;
  const DeltaIcon = rising ? ArrowUp : ArrowDown;

  const header = (
    <span className="flex min-w-0 items-center gap-2">
      {Icon ? (
        <Icon
          className={cn('size-[18px] shrink-0 stroke-[1.75]', ICON_TONE[iconTone])}
          aria-hidden
        />
      ) : null}
      <p className="truncate text-[15px] font-medium text-white">{label}</p>
      {hint ? (
        <CircleHelp className="size-3.5 shrink-0 text-muted-foreground/80" aria-hidden />
      ) : null}
    </span>
  );

  return (
    <Card className={cn('border-0 bg-[#2a2a2e] shadow-none', className)}>
      <CardContent className="flex min-h-[132px] flex-col gap-3 p-5">
        <div className="flex min-w-0 items-center gap-2">
          {hint ? (
            <HoverHint
              text={hint}
              label={`Where ${label} comes from. ${hint}`}
              className="min-w-0 cursor-help"
            >
              {header}
            </HoverHint>
          ) : (
            header
          )}
          {unavailable ? (
            <Badge
              variant="outline"
              className="ml-auto border-amber-500/40 bg-amber-500/10 text-[10px] font-medium uppercase tracking-wide text-amber-300"
            >
              Unavailable
            </Badge>
          ) : comingSoon ? (
            <Badge
              variant="outline"
              className="ml-auto border-sky-500/40 bg-sky-500/10 text-[10px] font-medium uppercase tracking-wide text-sky-300"
            >
              Soon
            </Badge>
          ) : null}
        </div>

        <p
          className={cn(
            'text-[32px] font-bold leading-none tracking-tight tabular-nums text-white',
            shell && 'text-zinc-500',
          )}
        >
          {value}
        </p>

        {hasDelta ? (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium tabular-nums',
                rising ? 'text-emerald-400' : 'text-rose-400',
              )}
            >
              <DeltaIcon className="size-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
              {Math.abs(delta!).toFixed(1)}%
            </span>
            {comparisonLabel ? <span className="text-muted-foreground">vs {comparisonLabel}</span> : null}
          </p>
        ) : unavailable ? (
          <p className="text-sm text-muted-foreground">PostHog unavailable</p>
        ) : comingSoon ? (
          <p className="text-sm text-muted-foreground">PostHog visitors — coming soon</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
